/**
 * API Client - Single HTTP client used by both web and mobile apps.
 *
 * This abstraction ensures:
 * 1. Web app and mobile apps hit the same endpoints
 * 2. Auth tokens are handled consistently
 * 3. Offline queue support for mobile
 * 4. Automatic retry with exponential backoff
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface ApiRequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  data?: any;
  params?: Record<string, string>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

interface ApiError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;
  private refreshToken: string | null = null;
  private offlineQueue: ApiRequestConfig[] = [];

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.loadTokens();

    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.processOfflineQueue());
    }
  }

  // ===== Auth Token Management =====
  setTokens(authToken: string, refreshToken?: string) {
    this.authToken = authToken;
    this.refreshToken = refreshToken || null;
    localStorage.setItem('akt_auth_token', authToken);
    if (refreshToken) {
      localStorage.setItem('akt_refresh_token', refreshToken);
    }
  }

  clearTokens() {
    this.authToken = null;
    this.refreshToken = null;
    localStorage.removeItem('akt_auth_token');
    localStorage.removeItem('akt_refresh_token');
  }

  private loadTokens() {
    if (typeof window !== 'undefined') {
      this.authToken = localStorage.getItem('akt_auth_token');
      this.refreshToken = localStorage.getItem('akt_refresh_token');
    }
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  isAuthenticated(): boolean {
    return this.authToken !== null;
  }

  // ===== Core Request Method =====
  private async request<T>(config: ApiRequestConfig): Promise<ApiResponse<T>> {
    const { method, endpoint, data, params, headers, signal } = config;

    // Build URL with query params
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (this.authToken) {
      requestHeaders['Authorization'] = `Bearer ${this.authToken}`;
    }

    // Check if offline — queue the request
    if (typeof navigator !== 'undefined' && !navigator.onLine && method !== 'GET') {
      this.offlineQueue.push(config);
      this.saveOfflineQueue();
      throw new OfflineError('Request queued for when connection is restored');
    }

    try {
      const response = await fetch(url.toString(), {
        method,
        headers: requestHeaders,
        body: data ? JSON.stringify(data) : undefined,
        signal,
      });

      // Handle 401 - try to refresh token
      if (response.status === 401 && this.refreshToken) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          // Retry the original request with new token
          requestHeaders['Authorization'] = `Bearer ${this.authToken}`;
          const retryResponse = await fetch(url.toString(), {
            method,
            headers: requestHeaders,
            body: data ? JSON.stringify(data) : undefined,
            signal,
          });
          return this.handleResponse<T>(retryResponse);
        }
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof OfflineError) throw error;
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiRequestError(0, 'Request was cancelled');
      }
      throw new ApiRequestError(0, 'Network error - please check your connection');
    }
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type');
    let responseData: any;

    if (contentType?.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      throw new ApiRequestError(
        response.status,
        responseData?.message || `HTTP ${response.status}`,
        responseData?.errors
      );
    }

    return {
      data: responseData?.data ?? responseData,
      status: response.status,
      message: responseData?.message,
    };
  }

  private async tryRefreshToken(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setTokens(data.authToken, data.refreshToken);
        return true;
      }
    } catch {
      // Refresh failed
    }

    this.clearTokens();
    return false;
  }

  // ===== Offline Queue =====
  private saveOfflineQueue() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('akt_offline_queue', JSON.stringify(this.offlineQueue));
    }
  }

  private loadOfflineQueue() {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('akt_offline_queue');
      if (saved) {
        this.offlineQueue = JSON.parse(saved);
      }
    }
  }

  private async processOfflineQueue() {
    this.loadOfflineQueue();
    const queue = [...this.offlineQueue];
    this.offlineQueue = [];
    this.saveOfflineQueue();

    for (const config of queue) {
      try {
        await this.request(config);
      } catch (error) {
        // Re-queue if still failing
        this.offlineQueue.push(config);
      }
    }
    this.saveOfflineQueue();
  }

  // ===== Convenience Methods =====
  async get<T>(endpoint: string, params?: Record<string, string>, signal?: AbortSignal) {
    return this.request<T>({ method: 'GET', endpoint, params, signal });
  }

  async post<T>(endpoint: string, data?: any) {
    return this.request<T>({ method: 'POST', endpoint, data });
  }

  async put<T>(endpoint: string, data?: any) {
    return this.request<T>({ method: 'PUT', endpoint, data });
  }

  async patch<T>(endpoint: string, data?: any) {
    return this.request<T>({ method: 'PATCH', endpoint, data });
  }

  async delete<T>(endpoint: string) {
    return this.request<T>({ method: 'DELETE', endpoint });
  }
}

// ===== Error Classes =====
export class ApiRequestError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(status: number, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.errors = errors;
  }
}

export class OfflineError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OfflineError';
  }
}

// Singleton instance
const apiClient = new ApiClient();
export default apiClient;
export { ApiClient };
export type { ApiResponse, ApiError, ApiRequestConfig };
